import React, { useState, useEffect } from 'react';
import { GoogleMap, useLoadScript, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import { AdminOrderDetailsResponse, LatLngPoint, OrderStatus } from '../../types/orders';
import { DriverLocationResponse } from '../../types/drivers';
import './OrderMap.css';

interface OrderMapProps {
  orderDetails: AdminOrderDetailsResponse;
  nearbyDrivers?: DriverLocationResponse[];
  driverPath?: LatLngPoint[];
}

const containerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '8px'
};

const OrderMap: React.FC<OrderMapProps> = ({ orderDetails, nearbyDrivers = [], driverPath = [] }) => {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
  });

  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [mapZoom, setMapZoom] = useState(13);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  useEffect(() => {
    // Calculate map center based on order data
    if (orderDetails.assignedRestaurantBranch) {
      setMapCenter({
        lat: orderDetails.assignedRestaurantBranch.latitude,
        lng: orderDetails.assignedRestaurantBranch.longitude
      });
    } else if (orderDetails.allRestaurantBranches.length > 0) {
      setMapCenter({
        lat: orderDetails.allRestaurantBranches[0].latitude,
        lng: orderDetails.allRestaurantBranches[0].longitude
      });
    } else {
      setMapCenter({
        lat: orderDetails.clientLatitude,
        lng: orderDetails.clientLongitude
      });
    }

  }, [orderDetails]);

  // Convert LatLngPoint to Google Maps LatLng format
  const toGoogleLatLng = (point: LatLngPoint) => ({
    lat: point.latitude,
    lng: point.longitude
  });

  // Determine if we should show all branches (order is pending)
  const showAllBranches = orderDetails.orderStatus === OrderStatus.ORDER_PLACED;

  // Client marker
  const clientLocation = {
    lat: orderDetails.clientLatitude,
    lng: orderDetails.clientLongitude
  };

  // Polyline options for driver path
  const pathOptions = {
    strokeColor: '#007bff',
    strokeOpacity: 0.8,
    strokeWeight: 4,
    geodesic: true
  };

  // Helper function to create marker icons with custom symbols
  const createMarkerIcon = (type: 'client' | 'restaurant' | 'driver' | 'branch' | 'nearbyDriver', size: number = 40) => {
    if (!isLoaded || !window.google) return undefined;

    // Using Google Maps KML shape icons with custom styling
    const iconConfigs = {
      client: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: '#dc3545',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 10,
        anchor: new window.google.maps.Point(0, 0),
        labelOrigin: new window.google.maps.Point(0, 0)
      },
      restaurant: {
        path: 'M12,2C8.13,2 5,5.13 5,9c0,5.25 7,13 7,13s7,-7.75 7,-13c0,-3.87 -3.13,-7 -7,-7zM12,11.5c-1.38,0 -2.5,-1.12 -2.5,-2.5s1.12,-2.5 2.5,-2.5 2.5,1.12 2.5,2.5 -1.12,2.5 -2.5,2.5z',
        fillColor: '#28a745',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.8,
        anchor: new window.google.maps.Point(12, 22),
        labelOrigin: new window.google.maps.Point(12, 9)
      },
      driver: {
        path: 'M18.92,6.01C18.72,5.42 18.16,5 17.5,5h-11C5.84,5 5.29,5.42 5.08,6.01L3,12v8c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-1h12v1c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-8L18.92,6.01zM6.5,16C5.67,16 5,15.33 5,14.5S5.67,13 6.5,13 8,13.67 8,14.5 7.33,16 6.5,16zM17.5,16c-0.83,0 -1.5,-0.67 -1.5,-1.5S16.67,13 17.5,13 19,13.67 19,14.5 18.33,16 17.5,16zM5,11l1.5,-4.5h11L19,11H5z',
        fillColor: '#007bff',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.5,
        anchor: new window.google.maps.Point(12, 12),
        labelOrigin: new window.google.maps.Point(12, 12)
      },
      nearbyDriver: {
        path: 'M18.92,6.01C18.72,5.42 18.16,5 17.5,5h-11C5.84,5 5.29,5.42 5.08,6.01L3,12v8c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-1h12v1c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-8L18.92,6.01zM6.5,16C5.67,16 5,15.33 5,14.5S5.67,13 6.5,13 8,13.67 8,14.5 7.33,16 6.5,16zM17.5,16c-0.83,0 -1.5,-0.67 -1.5,-1.5S16.67,13 17.5,13 19,13.67 19,14.5 18.33,16 17.5,16zM5,11l1.5,-4.5h11L19,11H5z',
        fillColor: '#ff6b35',
        fillOpacity: 0.85,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.5,
        anchor: new window.google.maps.Point(12, 12),
        labelOrigin: new window.google.maps.Point(12, 12)
      },
      branch: {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: '#ffc107',
        fillOpacity: 0.8,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 8,
        anchor: new window.google.maps.Point(0, 0),
        labelOrigin: new window.google.maps.Point(0, 0)
      }
    };

    return iconConfigs[type];
  };

  // Callback when map is loaded
  const onMapLoad = (mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  };

  // Effect to fit bounds when data changes - use a ref to prevent infinite loops
  const hasFitBounds = React.useRef(false);

  useEffect(() => {
    if (!map || !window.google || !isLoaded) return;

    // Only fit bounds once when map loads or when driver path changes significantly
      if (!Array.isArray(driverPath) || (hasFitBounds.current && driverPath.length === 0 && nearbyDrivers.length === 0)) return;
    hasFitBounds.current = true;

    const bounds = new window.google.maps.LatLngBounds();

    // Add client location
    bounds.extend({
      lat: orderDetails.clientLatitude,
      lng: orderDetails.clientLongitude
    });

    // Add assigned branch or all branches
    if (orderDetails.assignedRestaurantBranch) {
      bounds.extend({
        lat: orderDetails.assignedRestaurantBranch.latitude,
        lng: orderDetails.assignedRestaurantBranch.longitude
      });
    } else if (orderDetails.orderStatus === OrderStatus.ORDER_PLACED) {
      orderDetails.allRestaurantBranches.forEach(branch => {
        bounds.extend({ lat: branch.latitude, lng: branch.longitude });
      });
    }

    // Add driver path points
    if (driverPath.length > 0) {
      driverPath.forEach(point => {
        bounds.extend({ lat: point.latitude, lng: point.longitude });
      });
    }

    // Add nearby drivers to bounds
    if (nearbyDrivers.length > 0) {
      nearbyDrivers.forEach(driver => {
        bounds.extend({ lat: driver.latitude, lng: driver.longitude });
      });
    }

    // Fit map to bounds with some padding
    map.fitBounds(bounds, 50);
  }, [map, isLoaded, orderDetails.id, driverPath.length, nearbyDrivers.length]);

  if (loadError) {
    return <div style={{ padding: '1rem' }}>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div style={{ padding: '1rem' }}>Loading maps...</div>;
  }

  return (
    <div style={{ position: 'relative' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={onMapLoad}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {isLoaded && (
          <>
            {/* Client Location Marker */}
            <Marker
              position={clientLocation}
              title="Client Location"
              icon={createMarkerIcon('client')}
              onClick={() => setSelectedMarker('client')}
              zIndex={1000}
            />
            {selectedMarker === 'client' && (
              <InfoWindow
                position={clientLocation}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div>
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>Client Location</h4>
                  <p style={{ margin: 0, fontSize: '0.9rem' }}>{orderDetails.clientLocationName}</p>
                  {orderDetails.client.name && (
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                      {orderDetails.client.name}
                    </p>
                  )}
                </div>
              </InfoWindow>
            )}

            {/* Assigned Branch Marker */}
            {orderDetails.assignedRestaurantBranch && (
              <>
                <Marker
                  position={{
                    lat: orderDetails.assignedRestaurantBranch.latitude,
                    lng: orderDetails.assignedRestaurantBranch.longitude
                  }}
                  title={`Restaurant: ${orderDetails.assignedRestaurantBranch.name}`}
                  icon={createMarkerIcon('restaurant')}
                  onClick={() => setSelectedMarker('assigned-branch')}
                  zIndex={1001}
                />
                {selectedMarker === 'assigned-branch' && (
                  <InfoWindow
                    position={{
                      lat: orderDetails.assignedRestaurantBranch.latitude,
                      lng: orderDetails.assignedRestaurantBranch.longitude
                    }}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>Assigned Branch</h4>
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>
                        {orderDetails.assignedRestaurantBranch.name}
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                        {orderDetails.assignedRestaurantBranch.representativeName}
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                        {orderDetails.assignedRestaurantBranch.phone}
                      </p>
                    </div>
                  </InfoWindow>
                )}
              </>
            )}

            {/* All Branches Markers (only when order is pending) */}
            {showAllBranches && orderDetails.allRestaurantBranches.map((branch) => (
              <React.Fragment key={branch.id}>
                <Marker
                  position={{
                    lat: branch.latitude,
                    lng: branch.longitude
                  }}
                  title={branch.name}
                  icon={createMarkerIcon('branch')}
                  onClick={() => setSelectedMarker(`branch-${branch.id}`)}
                />
                {selectedMarker === `branch-${branch.id}` && (
                  <InfoWindow
                    position={{
                      lat: branch.latitude,
                      lng: branch.longitude
                    }}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>Available Branch</h4>
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>{branch.name}</p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                        {branch.representativeName}
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                        {branch.phone}
                      </p>
                    </div>
                  </InfoWindow>
                )}
              </React.Fragment>
            ))}

            {/* Driver Current Location (last point in path) */}
            {orderDetails.assignedDriver && driverPath.length > 0 && (
              <>
                <Marker
                  position={toGoogleLatLng(driverPath[driverPath.length - 1])}
                  title={`Driver: ${orderDetails.assignedDriver.fullName}`}
                  icon={createMarkerIcon('driver')}
                  onClick={() => setSelectedMarker('driver')}
                />
                {selectedMarker === 'driver' && (
                  <InfoWindow
                    position={toGoogleLatLng(driverPath[driverPath.length - 1])}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>Driver Location</h4>
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>
                        {orderDetails.assignedDriver.fullName}
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                        {orderDetails.assignedDriver.phone}
                      </p>
                    </div>
                  </InfoWindow>
                )}
              </>
            )}

            {/* Driver Path Polyline */}
            {driverPath.length > 1 && (
              <Polyline
                path={driverPath.map(toGoogleLatLng)}
                options={pathOptions}
              />
            )}

            {/* Nearby Available Drivers (shown when order accepted but no driver assigned) */}
            {nearbyDrivers.map((driver) => (
              <React.Fragment key={`nearby-driver-${driver.driverId}`}>
                <Marker
                  position={{
                    lat: driver.latitude,
                    lng: driver.longitude
                  }}
                  title={`Driver ID: ${driver.driverId}`}
                  icon={createMarkerIcon('nearbyDriver')}
                  onClick={() => setSelectedMarker(`nearby-driver-${driver.driverId}`)}
                  zIndex={500}
                />
                {selectedMarker === `nearby-driver-${driver.driverId}` && (
                  <InfoWindow
                    position={{
                      lat: driver.latitude,
                      lng: driver.longitude
                    }}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>Available Driver</h4>
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>
                        Driver ID: {driver.driverId}
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                        Status: {driver.appState === 'FOREGROUND' ? '🟢 Online' : '🔴 Offline'}
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                        <a
                          href={`/drivers/${driver.driverId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#007bff', textDecoration: 'none' }}
                        >
                          View Driver Details →
                        </a>
                      </p>
                    </div>
                  </InfoWindow>
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </GoogleMap>

      {/* Map Legend */}
      <div className="map-legend">
      <div className="map-legend-title">Map Legend</div>
      <div className="map-legend-item">
        <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
          <circle cx="12" cy="12" r="10" fill="#dc3545" stroke="#ffffff" strokeWidth="2"/>
        </svg>
        <span>Client Location</span>
      </div>
      <div className="map-legend-item">
        <svg width="20" height="24" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
          <path d="M12,2C8.13,2 5,5.13 5,9c0,5.25 7,13 7,13s7,-7.75 7,-13c0,-3.87 -3.13,-7 -7,-7zM12,11.5c-1.38,0 -2.5,-1.12 -2.5,-2.5s1.12,-2.5 2.5,-2.5 2.5,1.12 2.5,2.5 -1.12,2.5 -2.5,2.5z"
                fill="#28a745" stroke="#ffffff" strokeWidth="1.5"/>
        </svg>
        <span>Restaurant</span>
      </div>
      {orderDetails.assignedDriver && (
        <div className="map-legend-item">
          <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
            <path d="M18.92,6.01C18.72,5.42 18.16,5 17.5,5h-11C5.84,5 5.29,5.42 5.08,6.01L3,12v8c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-1h12v1c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-8L18.92,6.01zM6.5,16C5.67,16 5,15.33 5,14.5S5.67,13 6.5,13 8,13.67 8,14.5 7.33,16 6.5,16zM17.5,16c-0.83,0 -1.5,-0.67 -1.5,-1.5S16.67,13 17.5,13 19,13.67 19,14.5 18.33,16 17.5,16zM5,11l1.5,-4.5h11L19,11H5z"
                  fill="#007bff" stroke="#ffffff" strokeWidth="1"/>
          </svg>
          <span>Driver & Path</span>
        </div>
      )}
      {showAllBranches && (
        <div className="map-legend-item">
          <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
            <circle cx="12" cy="12" r="8" fill="#ffc107" fillOpacity="0.8" stroke="#ffffff" strokeWidth="2"/>
          </svg>
          <span>Available Branches</span>
        </div>
      )}
      {nearbyDrivers.length > 0 && (
        <div className="map-legend-item">
          <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
            <path d="M18.92,6.01C18.72,5.42 18.16,5 17.5,5h-11C5.84,5 5.29,5.42 5.08,6.01L3,12v8c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-1h12v1c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-8L18.92,6.01zM6.5,16C5.67,16 5,15.33 5,14.5S5.67,13 6.5,13 8,13.67 8,14.5 7.33,16 6.5,16zM17.5,16c-0.83,0 -1.5,-0.67 -1.5,-1.5S16.67,13 17.5,13 19,13.67 19,14.5 18.33,16 17.5,16zM5,11l1.5,-4.5h11L19,11H5z"
                  fill="#ff6b35" fillOpacity="0.85" stroke="#ffffff" strokeWidth="1"/>
          </svg>
          <span>Nearby Available Drivers ({nearbyDrivers.length})</span>
        </div>
      )}
    </div>
  </div>
  );
};

export default OrderMap;
